/**
 * @fileoverview Shared CSV build + download helpers for selected-row exports
 * and grid export actions.
 * @module ui-admin/utils
 *
 * Responsibilities:
 * - buildCsv() — escape + join headers and row cells into CSV text.
 * - downloadCsv() — build a CSV from headers/rows and trigger a browser
 *   download; a no-op when rows is empty so stale selections can't produce
 *   header-only files.
 * - downloadBlob() — trigger a browser download for any prepared Blob.
 *
 * Dependencies:
 * - None (host-neutral DOM APIs only).
 *
 * Architecture:
 * - Pure utility. Used by grid bulk actions (e.g. LookupsPage category/value
 *   exports) instead of per-page CSV builders.
 *
 * Notes:
 * - Every cell is wrapped in double quotes with inner quotes doubled — uniform
 *   escaping keeps commas/newlines/quotes safe without per-call-site logic.
 */

function escapeCsvCell(value: unknown): string {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

/**
 * @process buildCsv
 * Steps:    escape each header/cell → join rows with commas → join lines with \n.
 * Inputs:   headers, rows (unknown[][]; cells are stringified, null/undefined → '').
 * Outputs:  CSV text.
 * Idempotency: NOT REQUIRED (pure function).
 */
export function buildCsv(headers: string[], rows: unknown[][]): string {
  const lines = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(','));
  return lines.join('\n');
}

/**
 * @process downloadBlob
 * Steps:    createObjectURL → anchor click → revokeObjectURL.
 * Inputs:   blob, filename.
 * Outputs:  Browser download.
 * Idempotency: NOT REQUIRED.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * @process downloadCsv
 * Steps:    guard empty rows → buildCsv → Blob → downloadBlob.
 * Inputs:   filename, headers, rows (empty rows → no download; a header-only
 *           file would present as a successful export of nothing).
 * Outputs:  Browser download of a .csv file.
 * Idempotency: NOT REQUIRED.
 */
export function downloadCsv(filename: string, headers: string[], rows: unknown[][]): void {
  if (rows.length === 0) return;
  downloadBlob(new Blob([buildCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' }), filename);
}
